import React, { useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  Text,
  View,
  Page,
  Document,
  StyleSheet,
  Svg,
  Path,
  Font,
  Image
} from '@react-pdf/renderer';
import { BookingLine } from 'services/api/type/booking.type';
import { formatCurrency, toLocalTime } from 'utils';
import { useTranslation } from 'react-i18next';
// import { PAYMENT_METHOD_NAMES, PAYMENT_STATUS } from "constants/form";
// import { currencyFormatter } from "utils/currency";

// Register font
Font.register({
  family: 'Inter',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyeMZhrib2Bg-4.ttf',
      fontWeight: 100
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyfMZhrib2Bg-4.ttf',
      fontWeight: 200
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuOKfMZhrib2Bg-4.ttf',
      fontWeight: 300
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf',
      fontWeight: 400
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuI6fMZhrib2Bg-4.ttf',
      fontWeight: 500
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYMZhrib2Bg-4.ttf',
      fontWeight: 600
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYMZhrib2Bg-4.ttf',
      fontWeight: 700
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyYMZhrib2Bg-4.ttf',
      fontWeight: 800
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuBWYMZhrib2Bg-4.ttf',
      fontWeight: 900
    }
  ]
});

const borderColor = '#000';
const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    paddingTop: 20,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
    lineHeight: 1.5,
    flexDirection: 'column'
  },

  spaceBetween: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#000'
  },
  flex: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px'
  },
  center: {
    flexDirection: 'row',
    justifyContent: 'center'
  },
  square: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'black',
    width: 20,
    height: 20
  },

  headerContainer: { flexDirection: 'column', marginBottom: 4 },
  headerLogo: { flexDirection: 'row', justifyContent: 'center' },

  tableContainer: {
    flexDirection: 'column',
    // flexWrap: "wrap",
    marginTop: 4,
    borderWidth: 1,
    borderColor: borderColor,
    borderBottom: 0,
    borderRight: 0
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomColor: borderColor,
    borderBottomWidth: 1,
    minHeight: 28
  },
  tableCell: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRightColor: borderColor,
    borderRightWidth: 1,
    color: '#000',
    padding: '6px 8px'
  },

  signatureContainer: {
    flexDirection: 'row',
    marginTop: 28,
    justifyContent: 'space-evenly'
  },
  signatureItem: {
    flexDirection: 'column',
    rowGap: '8px'
  },
  image: {
    width: 60,
    height: 50,
    marginTop: 10
  },

  logo: { width: 110 },
  text: { fontSize: 10, fontWeight: 500, fontFamily: 'Inter' },
  textExSmallBold: { fontSize: 9, fontWeight: 600, fontFamily: 'Inter' },
  textBold: { fontSize: 13, fontWeight: 600, fontFamily: 'Inter' },
  textSmallBold: { fontSize: 11, fontWeight: 600, fontFamily: 'Inter' }
});

interface Props {
  dataChange: any;
  bookingLineDetail: BookingLine | undefined;
  signatureStaff: string;
  signatureGuest: string;
}

export default function NikConfirmationPDF({
  dataChange,
  bookingLineDetail,
  signatureStaff,
  signatureGuest
}: Props) {
  const { t } = useTranslation();
  const guestInfo = useMemo(() => {
    return dataChange.isShowGuest
      ? bookingLineDetail?.booking_line_guests.find(item => !item.main_guest)
      : bookingLineDetail?.booking_line_guests.find(item => item.main_guest);
  }, [bookingLineDetail?.booking_line_guests, dataChange.isShowGuest]);

  const firstName = useMemo(() => {
    const arrWords = guestInfo?.guest_name.split(' ');
    if (arrWords) {
      return _.get(arrWords, arrWords.length - 1);
    }
    return '';
  }, [guestInfo]);

  const lastName = useMemo(() => {
    const arrWords = guestInfo?.guest_name.split(' ');
    return arrWords?.slice(0, arrWords.length - 1).join(' ');
  }, [guestInfo]);

  const Logo = () => (
    <Svg width="110" height="90" viewBox="0 0 1255 836" preserveAspectRatio="xMidYMid meet">
      <Path
        d="M 240.50 324.50 L 239.50 506.50 L 141.00 507.02 L 141.00 405.73 C141.00,301.87 141.23,294.87 145.06,279.50 C147.82,268.42 157.51,249.28 165.40,239.33 C172.76,230.04 184.47,219.64 193.96,213.95 C197.51,211.82 242.96,189.37 294.96,164.06 C397.53,114.13 397.84,114.00 414.09,114.00 C434.12,114.00 450.93,120.81 464.43,134.38 C477.24,147.27 482.97,159.15 485.01,177.06 C485.74,183.51 485.99,237.16 485.79,346.50 L 485.50 506.50 L 436.27 506.76 L 387.03 507.02 L 386.77 391.76 L 386.50 276.50 L 383.70 270.50 C375.74,253.46 358.44,246.07 340.37,252.02 C336.59,253.26 317.84,262.11 298.71,271.69 C266.45,287.83 263.45,289.56 257.62,295.30 C250.67,302.15 246.96,307.92 243.08,317.88 ZM 1113.37 506.30 C1112.98,506.69 1085.99,507.00 1053.39,507.00 L 994.10 507.00 L 928.80 437.23 C892.89,398.86 860.24,363.94 856.25,359.63 C843.09,345.41 837.59,330.79 837.57,310.00 C837.56,295.82 838.94,289.79 844.71,278.75 C848.34,271.82 851.68,267.85 942.49,162.65 L 984.49 114.00 L 1043.87 114.00 C1099.87,114.00 1103.17,114.10 1101.87,115.72 C1100.57,117.34 1026.99,195.20 991.03,233.00 C945.54,280.82 938.46,288.65 935.17,294.82 C929.16,306.11 929.81,314.70 937.60,326.73 C939.99,330.42 972.52,363.75 1027.70,419.05 C1075.20,466.65 1113.75,505.92 1113.37,506.30 ZM 817.75 316.14 L 817.50 506.50 L 768.75 506.76 L 720.00 507.02 L 720.00 152.11 L 758.65 133.06 C791.35,116.93 797.98,114.00 801.76,114.00 C807.57,114.00 813.17,117.18 815.88,122.01 L 818.01 125.78 ZM 649.75 328.00 L 649.50 506.50 L 600.75 506.76 L 552.00 507.02 L 552.02 343.26 L 552.03 179.50 L 590.00 159.25 C626.77,139.63 628.14,139.00 633.73,139.00 C640.49,139.01 644.98,141.29 648.01,146.24 C649.97,149.47 649.99,151.37 649.75,328.00 ZM 439.00 722.53 C427.39,724.11 391.48,724.32 379.05,722.88 C348.36,719.32 330.23,704.82 324.03,678.89 C319.23,658.79 319.22,618.27 324.01,599.38 C328.43,581.96 336.77,571.17 351.37,563.97 C365.47,557.02 385.64,554.38 417.53,555.31 C448.59,556.22 465.10,560.81 477.17,571.89 C491.59,585.13 496.87,606.46 495.70,646.88 C494.80,678.15 490.22,693.73 478.47,705.48 C468.71,715.25 457.75,719.98 439.00,722.53 ZM 183.76 687.25 L 183.50 721.50 L 141.00 722.04 L 141.00 557.00 L 184.00 557.00 L 184.00 620.03 L 255.50 619.50 L 256.03 557.00 L 299.00 557.00 L 299.00 722.00 L 279.08 722.00 C268.13,722.00 258.45,721.73 257.58,721.39 C256.18,720.86 256.00,716.88 256.00,686.89 L 256.00 653.00 L 184.03 653.00 ZM 818.05 690.00 L 817.50 721.50 L 756.38 721.76 L 695.26 722.02 L 689.89 719.21 C685.36,716.85 684.04,715.47 681.51,710.52 L 678.50 704.64 L 678.50 573.50 L 681.00 568.50 C683.67,563.16 687.98,559.66 693.88,558.02 C696.12,557.39 720.32,557.01 757.28,557.01 L 817.05 557.00 L 816.50 588.50 L 770.78 589.00 C728.65,589.46 724.93,589.64 723.28,591.28 C721.76,592.80 721.45,595.28 721.18,608.03 L 720.86 623.00 L 815.05 623.00 L 814.78 638.25 L 814.50 653.50 L 721.00 654.02 L 721.00 684.15 L 723.92 687.08 L 726.85 690.00 ZM 604.00 590.00 L 604.00 722.00 L 583.17 722.00 C571.71,722.00 562.03,721.70 561.67,721.33 C561.30,720.97 561.00,691.27 561.00,655.33 L 561.00 590.00 L 500.00 590.00 L 500.00 557.00 L 665.00 557.00 L 665.00 590.00 ZM 978.05 689.00 L 977.50 721.50 L 917.00 721.73 C859.89,721.95 856.28,721.86 852.50,720.11 C850.30,719.09 847.29,716.96 845.80,715.38 C844.94,714.45 844.20,713.72 843.57,712.91 C839.99,708.27 839.99,700.86 840.00,638.73 C840.00,636.25 840.00,633.69 840.00,631.03 L 840.00 557.00 L 881.96 557.00 L 882.23 620.59 L 882.50 684.17 L 885.31 686.59 L 888.11 689.00 ZM 390.76 689.50 C398.67,691.50 422.53,690.92 429.50,688.55 C444.74,683.37 451.11,671.75 452.58,646.50 C453.78,625.69 450.14,607.03 443.32,599.06 C436.40,590.97 421.98,587.17 402.57,588.31 C372.82,590.07 364.12,601.51 364.04,639.00 C364.00,660.44 366.86,672.52 373.80,680.20 C377.99,684.84 382.85,687.50 390.76,689.50 ZM 1113.00 721.00 C1110.46,723.54 1105.98,723.56 1102.78,721.04 C1101.40,719.96 1095.45,711.41 1089.55,702.04 C1083.65,692.67 1078.41,685.00 1077.91,685.00 C1077.40,685.00 1077.00,693.01 1077.00,703.50 L 1077.00 722.00 L 1068.00 722.00 L 1068.00 698.56 C1068.00,675.46 1068.03,675.09 1070.22,673.56 C1073.01,671.60 1077.92,671.55 1080.63,673.45 C1081.77,674.25 1087.60,682.58 1093.59,691.95 C1099.57,701.33 1104.82,709.00 1105.24,709.00 C1105.66,709.00 1106.00,700.90 1106.00,691.00 L 1106.00 673.00 L 1115.00 673.00 L 1115.00 696.00 C1115.00,717.67 1114.88,719.12 1113.00,721.00 ZM 1023.79 635.70 C1020.99,638.39 1020.42,638.51 1009.16,638.83 C1008.17,638.86 1007.24,638.89 1006.37,638.92 C1001.29,639.10 998.28,639.21 996.51,637.78 C993.95,635.72 993.96,630.46 993.99,617.57 C994.00,616.05 994.00,614.42 994.00,612.67 L 994.00 590.00 L 1001.90 590.00 L 1002.20 595.75 L 1002.50 601.50 L 1012.17 602.00 C1020.48,602.43 1022.19,602.82 1024.28,604.79 C1030.90,611.02 1030.61,629.16 1023.79,635.70 ZM 1064.10 627.24 L 1053.21 651.50 L 1049.82 651.80 C1047.95,651.96 1046.15,651.81 1045.80,651.47 C1045.46,651.13 1046.26,648.39 1047.59,645.39 C1048.92,642.40 1050.00,639.34 1050.00,638.61 C1050.00,637.88 1046.44,629.34 1042.09,619.64 L 1034.18 602.00 L 1042.73 602.00 L 1048.19 615.07 C1051.20,622.26 1054.05,628.15 1054.53,628.16 C1055.01,628.17 1057.95,622.40 1061.07,615.34 L 1066.73 602.50 L 1070.87 602.19 C1073.14,602.02 1075.00,602.13 1075.00,602.43 C1075.00,602.73 1070.10,613.89 1064.10,627.24 ZM 1039.38 721.19 C1039.11,721.63 1036.77,721.99 1034.19,721.98 C1029.67,721.96 1029.13,721.62 1019.00,712.36 C1010.34,704.45 1006.21,700.70 1006.19,696.94 C1006.18,693.53 1009.55,690.10 1016.00,683.54 L 1016.19 683.36 L 1026.38 673.00 L 1038.35 673.00 L 1026.68 684.09 C1020.25,690.18 1015.00,695.79 1015.00,696.55 C1015.00,697.30 1020.60,702.97 1027.44,709.15 C1034.29,715.32 1039.66,720.74 1039.38,721.19 ZM 1002.77 697.25 L 1002.50 721.50 L 994.00 722.11 L 994.00 673.00 L 1003.04 673.00 ZM 1003.20 631.80 C1005.17,633.77 1014.41,633.30 1017.14,631.09 C1019.22,629.40 1019.54,628.18 1019.85,620.69 C1020.16,613.13 1019.97,611.97 1018.10,610.10 C1016.37,608.37 1014.78,608.00 1009.00,608.00 L 1002.00 608.00 L 1002.00 619.30 C1002.00,626.76 1002.41,631.01 1003.20,631.80 ZM 1058.00 685.00 L 1058.00 722.00 L 1050.00 722.00 L 1050.00 685.00 ZM 1057.81 675.75 C1057.52,679.30 1057.30,679.52 1053.75,679.81 L 1050.00 680.12 L 1050.00 672.00 L 1058.12 672.00 Z"
        fill="rgb(26,71,42)"
      />
      <Path
        d="M 0.00 418.00 L 0.00 0.00 L 627.50 0.00 L 1255.00 0.00 L 1255.00 418.00 L 1255.00 836.00 L 627.50 836.00 L 0.00 836.00 L 0.00 418.00 ZM 439.00 722.53 C457.75,719.98 468.71,715.25 478.47,705.48 C490.22,693.73 494.80,678.15 495.70,646.88 C496.87,606.46 491.59,585.13 477.17,571.89 C465.10,560.81 448.59,556.22 417.53,555.31 C385.64,554.38 365.47,557.02 351.37,563.97 C336.77,571.17 328.43,581.96 324.01,599.38 C319.22,618.27 319.23,658.79 324.03,678.89 C330.23,704.82 348.36,719.32 379.05,722.88 C391.48,724.32 427.39,724.11 439.00,722.53 ZM 1113.00 721.00 C1114.88,719.12 1115.00,717.67 1115.00,696.00 L 1115.00 673.00 L 1110.50 673.00 L 1106.00 673.00 L 1106.00 691.00 C1106.00,700.90 1105.66,709.00 1105.24,709.00 C1104.82,709.00 1099.57,701.33 1093.59,691.95 C1087.60,682.58 1081.77,674.25 1080.63,673.45 C1077.92,671.55 1073.01,671.60 1070.22,673.56 C1068.03,675.09 1068.00,675.46 1068.00,698.56 L 1068.00 722.00 L 1072.50 722.00 L 1077.00 722.00 L 1077.00 703.50 C1077.00,693.01 1077.40,685.00 1077.91,685.00 C1078.41,685.00 1083.65,692.67 1089.55,702.04 C1095.45,711.41 1101.40,719.96 1102.78,721.04 C1105.98,723.56 1110.46,723.54 1113.00,721.00 ZM 183.76 687.25 L 184.03 653.00 L 220.01 653.00 L 256.00 653.00 L 256.00 686.89 C256.00,716.88 256.18,720.86 257.58,721.39 C258.45,721.73 268.13,722.00 279.08,722.00 L 299.00 722.00 L 299.00 639.50 L 299.00 557.00 L 277.51 557.00 L 256.03 557.00 L 255.76 588.25 L 255.50 619.50 L 219.75 619.76 L 184.00 620.03 L 184.00 588.51 L 184.00 557.00 L 162.50 557.00 L 141.00 557.00 L 141.00 639.52 L 141.00 722.04 L 162.25 721.77 L 183.50 721.50 L 183.76 687.25 ZM 604.00 656.00 L 604.00 590.00 L 634.50 590.00 L 665.00 590.00 L 665.00 573.50 L 665.00 557.00 L 582.50 557.00 L 500.00 557.00 L 500.00 573.50 L 500.00 590.00 L 530.50 590.00 L 561.00 590.00 L 561.00 655.33 C561.00,691.27 561.30,720.97 561.67,721.33 C562.03,721.70 571.71,722.00 583.17,722.00 L 604.00 722.00 L 604.00 656.00 ZM 817.78 705.75 L 818.05 690.00 L 772.45 690.00 L 726.85 690.00 L 723.92 687.08 L 721.00 684.15 L 721.00 669.09 L 721.00 654.02 L 767.75 653.76 L 814.50 653.50 L 814.78 638.25 L 815.05 623.00 L 767.95 623.00 L 720.86 623.00 L 721.18 608.03 C721.45,595.28 721.76,592.80 723.28,591.28 C724.93,589.64 728.65,589.46 770.78,589.00 L 816.50 588.50 L 816.78 572.75 L 817.05 557.00 L 757.28 557.01 C720.32,557.01 696.12,557.39 693.88,558.02 C687.98,559.66 683.67,563.16 681.00,568.50 L 678.50 573.50 L 678.50 639.07 L 678.50 704.64 L 681.51 710.52 C684.04,715.47 685.36,716.85 689.89,719.21 L 695.26 722.02 L 756.38 721.76 L 817.50 721.50 L 817.78 705.75 ZM 977.77 705.25 L 978.05 689.00 L 933.08 689.00 L 888.11 689.00 L 885.31 686.59 L 882.50 684.17 L 882.23 620.59 L 881.96 557.00 L 860.98 557.00 L 840.00 557.00 L 840.00 631.03 C840.00,711.93 839.80,708.97 845.80,715.38 C847.29,716.96 850.30,719.09 852.50,720.11 C856.28,721.86 859.89,721.95 917.00,721.73 L 977.50 721.50 L 977.77 705.25 ZM 1002.77 697.25 L 1003.04 673.00 L 998.52 673.00 L 994.00 673.00 L 994.00 697.56 L 994.00 722.11 L 998.25 721.81 L 1002.50 721.50 L 1002.77 697.25 ZM 1039.38 721.19 C1039.66,720.74 1034.29,715.32 1027.44,709.15 C1020.60,702.97 1015.00,697.30 1015.00,696.55 C1015.00,695.79 1020.25,690.18 1026.68,684.09 L 1038.35 673.00 L 1032.37 673.00 L 1026.38 673.00 L 1016.19 683.36 C1002.43,697.34 1002.42,697.21 1019.00,712.36 C1029.13,721.62 1029.67,721.96 1034.19,721.98 C1036.77,721.99 1039.11,721.63 1039.38,721.19 ZM 1058.00 703.50 L 1058.00 685.00 L 1054.00 685.00 L 1050.00 685.00 L 1050.00 703.50 L 1050.00 722.00 L 1054.00 722.00 L 1058.00 722.00 L 1058.00 703.50 ZM 1057.81 675.75 L 1058.12 672.00 L 1054.06 672.00 L 1050.00 672.00 L 1050.00 676.06 L 1050.00 680.12 L 1053.75 679.81 C1057.30,679.52 1057.52,679.30 1057.81,675.75 ZM 1064.10 627.24 C1070.10,613.89 1075.00,602.73 1075.00,602.43 C1075.00,602.13 1073.14,602.02 1070.87,602.19 L 1066.73 602.50 L 1061.07 615.34 C1057.95,622.40 1055.01,628.17 1054.53,628.16 C1054.05,628.15 1051.20,622.26 1048.19,615.07 L 1042.73 602.00 L 1038.46 602.00 L 1034.18 602.00 L 1042.09 619.64 C1046.44,629.34 1050.00,637.88 1050.00,638.61 C1050.00,639.34 1048.92,642.40 1047.59,645.39 C1046.26,648.39 1045.46,651.13 1045.80,651.47 C1046.15,651.81 1047.95,651.96 1049.82,651.80 L 1053.21 651.50 L 1064.10 627.24 ZM 1023.79 635.70 C1030.61,629.16 1030.90,611.02 1024.28,604.79 C1022.19,602.82 1020.48,602.43 1012.17,602.00 L 1002.50 601.50 L 1002.20 595.75 L 1001.90 590.00 L 997.95 590.00 L 994.00 590.00 L 994.00 612.67 C994.00,641.10 992.94,639.28 1009.16,638.83 C1020.42,638.51 1020.99,638.39 1023.79,635.70 ZM 240.00 415.50 L 240.50 324.50 L 243.08 317.88 C246.96,307.92 250.67,302.15 257.62,295.30 C263.45,289.56 266.45,287.83 298.71,271.69 C317.84,262.11 336.59,253.26 340.37,252.02 C358.44,246.07 375.74,253.46 383.70,270.50 L 386.50 276.50 L 386.77 391.76 L 387.03 507.02 L 436.27 506.76 L 485.50 506.50 L 485.79 346.50 C485.99,237.16 485.74,183.51 485.01,177.06 C482.97,159.15 477.24,147.27 464.43,134.38 C450.93,120.81 434.12,114.00 414.09,114.00 C397.84,114.00 397.53,114.13 294.96,164.06 C242.96,189.37 197.51,211.82 193.96,213.95 C184.47,219.64 172.76,230.04 165.40,239.33 C157.51,249.28 147.82,268.42 145.06,279.50 C141.23,294.87 141.00,301.87 141.00,405.73 L 141.00 507.02 L 190.25 506.76 L 239.50 506.50 L 240.00 415.50 ZM 649.75 328.00 C649.99,151.37 649.97,149.47 648.01,146.24 C644.98,141.29 640.49,139.01 633.73,139.00 C628.14,139.00 626.77,139.63 590.00,159.25 L 552.03 179.50 L 552.02 343.26 L 552.00 507.02 L 600.75 506.76 L 649.50 506.50 L 649.75 328.00 ZM 817.75 316.14 L 818.01 125.78 L 815.88 122.01 C813.17,117.18 807.57,114.00 801.76,114.00 C797.98,114.00 791.35,116.93 758.65,133.06 L 720.00 152.11 L 720.00 329.57 L 720.00 507.02 L 768.75 506.76 L 817.50 506.50 L 817.75 316.14 ZM 1113.37 506.30 C1113.75,505.92 1075.20,466.65 1027.70,419.05 C972.52,363.75 939.99,330.42 937.60,326.73 C929.81,314.70 929.16,306.11 935.17,294.82 C938.46,288.65 945.54,280.82 991.03,233.00 C1026.99,195.20 1100.57,117.34 1101.87,115.72 C1103.17,114.10 1099.87,114.00 1043.87,114.00 L 984.49 114.00 L 942.49 162.65 C851.68,267.85 848.34,271.82 844.71,278.75 C838.94,289.79 837.56,295.82 837.57,310.00 C837.59,330.79 843.09,345.41 856.25,359.63 C860.24,363.94 892.89,398.86 928.80,437.23 L 994.10 507.00 L 1053.39 507.00 C1085.99,507.00 1112.98,506.69 1113.37,506.30 ZM 390.76 689.50 C382.85,687.50 377.99,684.84 373.80,680.20 C366.86,672.52 364.00,660.44 364.04,639.00 C364.12,601.51 372.82,590.07 402.57,588.31 C421.98,587.17 436.40,590.97 443.32,599.06 C450.14,607.03 453.78,625.69 452.58,646.50 C451.11,671.75 444.74,683.37 429.50,688.55 C422.53,690.92 398.67,691.50 390.76,689.50 ZM 1003.20 631.80 C1002.41,631.01 1002.00,626.76 1002.00,619.30 L 1002.00 608.00 L 1009.00 608.00 C1014.78,608.00 1016.37,608.37 1018.10,610.10 C1019.97,611.97 1020.16,613.13 1019.85,620.69 C1019.54,628.18 1019.22,629.40 1017.14,631.09 C1014.41,633.30 1005.17,633.77 1003.20,631.80 Z"
        fill="rgb(254,254,254)"
      />
    </Svg>
  );
  const ListItem = ({ children }: any) => {
    return (
      <View style={{ ...styles.flex, gap: '4px', width: '98%' }}>
        <View style={{ height: '100%' }}>
          <Text>{'\u2022'}</Text>
        </View>
        <Text style={styles.text}>{children}</Text>
      </View>
    );
  };

  const HotelInfo = () => (
    <View style={styles.headerContainer}>
      <View style={styles.headerLogo}>
        <Logo />
      </View>
      <View style={{ ...styles.center, marginTop: 12 }}>
        <Text style={styles.textBold}>GUEST REGISTRATION FORM</Text>
      </View>
      <View style={styles.center}>
        <Text style={styles.textSmallBold}>Booking Id: {bookingLineDetail?.booking_id}</Text>
      </View>
    </View>
  );

  const TableInfo = () => (
    <View style={styles.tableContainer}>
      <View style={styles.tableRow}>
        <View style={{ ...styles.tableCell, width: '68%' }}>
          <Text style={styles.text}>Full name: {lastName + ' ' + firstName}</Text>
        </View>
        <View style={{ ...styles.tableCell, width: '34%' }}>
          <Text style={styles.text}>Nationality: {guestInfo?.country?.name}</Text>
        </View>
      </View>

      <View style={styles.tableRow}>
        <View style={{ ...styles.tableCell, width: '50%' }}>
          <Text style={styles.text}>Room type: {bookingLineDetail?.display_room_type_name}</Text>
        </View>
        <View style={{ ...styles.tableCell, width: '50%' }}>
          <Text style={styles.text}>Room number: {bookingLineDetail?.room_name}</Text>
        </View>
      </View>

      <View style={styles.tableRow}>
        <View style={{ ...styles.tableCell, width: '40%' }}>
          <Text style={styles.text}>
            Date of Birth: {guestInfo?.dob ? dayjs(guestInfo?.dob).format('DD/MM/YYYY') : ''}
          </Text>
        </View>
        <View style={{ ...styles.tableCell, width: '60%', columnGap: '20px' }}>
          <Text style={styles.text}>Sex</Text>

          <View style={styles.flex}>
            <View style={styles.square}>
              {guestInfo?.gender === 'male' && <Text style={styles.text}>X</Text>}
            </View>
            <Text style={styles.text}>Male</Text>
          </View>

          <View style={styles.flex}>
            <View style={styles.square}>
              {guestInfo?.gender === 'female' && <Text style={styles.text}>X</Text>}
            </View>
            <Text style={styles.text}>Female</Text>
          </View>
        </View>
      </View>

      {dataChange.isShowRoomRate && (
        <View style={styles.tableRow}>
          <View
            style={{
              ...styles.tableCell,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '4px',
              width: '100%'
            }}
          >
            <Text style={styles.text}>
              Room rate: {formatCurrency(bookingLineDetail?.subtotal_price)}
            </Text>
            <Text style={styles.text}>This rate is inclusive of 8% VAT and 5% service charge</Text>
          </View>
        </View>
      )}

      <View style={styles.tableRow}>
        <View style={{ ...styles.tableCell, width: '50%' }}>
          <Text style={styles.text}>Document Number: {guestInfo?.identification}</Text>
        </View>
        <View style={{ ...styles.tableCell, width: '50%' }}>
          <Text style={styles.text}>
            Booking Source: {bookingLineDetail?.hotel_travel_agency_name}
          </Text>
        </View>
      </View>

      <View style={styles.tableRow}>
        <View style={{ ...styles.tableCell, width: '50%' }}>
          <Text style={styles.text}>Email address: {dataChange?.email}</Text>
        </View>
        <View style={{ ...styles.tableCell, width: '50%' }}>
          <Text style={styles.text}>Phone number: {dataChange?.phone}</Text>
        </View>
      </View>

      <View style={styles.tableRow}>
        <View style={{ ...styles.tableCell, width: '50%' }}>
          <Text style={styles.text}>
            Date of Arrival:{' '}
            {bookingLineDetail?.check_in
              ? toLocalTime(bookingLineDetail?.check_in, 'DD/MM/YYYY')
              : ''}
          </Text>
        </View>
        <View style={{ ...styles.tableCell, width: '50%' }}>
          <Text style={styles.text}>
            Date of Departure:{' '}
            {bookingLineDetail?.check_in
              ? toLocalTime(bookingLineDetail?.check_out, 'DD/MM/YYYY')
              : ''}
          </Text>
        </View>
      </View>

      <View style={styles.tableRow}>
        <View style={{ ...styles.tableCell, width: '50%', columnGap: '20px' }}>
          <Text style={styles.text}>VAT Invoice (manual input)</Text>
          <View style={styles.flex}>
            <View style={styles.square}>
              {dataChange?.isShowVAT && <Text style={styles.text}>X</Text>}
            </View>
            <Text style={styles.text}>Yes</Text>
          </View>
          <View style={styles.flex}>
            <View style={styles.square}>
              {!dataChange?.isShowVAT && <Text style={styles.text}>X</Text>}
            </View>
            <Text style={styles.text}>No</Text>
          </View>
        </View>
        <View
          style={{
            ...styles.tableCell,
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
            width: '50%'
          }}
        >
          <Text style={styles.text}>
            Number of Guest: {bookingLineDetail?.booking_line_guests.length}
          </Text>
          <Text style={styles.text}>
            {!_.isEmpty(bookingLineDetail?.breakfast_dates)
              ? 'Breakfast Included'
              : 'Breakfast Is Not Included'}
          </Text>
        </View>
      </View>

      <View style={styles.tableRow}>
        <View
          style={{
            ...styles.tableCell,
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
            width: '100%'
          }}
        >
          <ListItem>
            <Text>
              Full payment for room charges is required prior to check-in. /{'\n'}
              Quý khách vui lòng thanh toán tất cả các khoản tiền phòng trước khi nhận phòng.
            </Text>
          </ListItem>

          <ListItem>
            <Text>Check-in time is 2 PM. / Giờ nhận phòng: 14:00.</Text>
          </ListItem>

          <ListItem>
            <Text>Check-out time is 12 PM. / Giờ trả phòng: 12:00.</Text>
          </ListItem>

          <ListItem>
            <Text>
              Early check-in or late check-out will incur additional fees as per hotel policy. /
              {'\n'}
              Nhận phòng sớm hoặc trả phòng trễ sẽ phụ thu phí theo chính sách của khách sạn.
            </Text>
          </ListItem>

          <ListItem>
            <Text>
              Smoking or bringing strong-smelling foods such as durian, shrimp paste, or seafood
              into the room is strictly prohibited. A cleaning fee of VND 2,000,000 will be applied
              for violations. /{'\n'}
              Không hút thuốc trong phòng hoặc mang những đồ ăn nặng mùi như sầu riêng, mắm tôm, hải
              sản,... vào phòng. Nếu vi phạm chúng tôi bắt buộc phải thu phí khử mùi phòng 2.000.000
              VNĐ.
            </Text>
          </ListItem>

          <ListItem>
            <Text>
              All guests must present a valid ID card, driver license, passport, temporary residence
              card, or birth certificate upon check-in. /{'\n'}
              Tất cả các khách lưu trú phải xuất trình căn cước công dân, bằng lái xe, hộ chiếu hoặc
              thẻ tạm trú, giấy khai sinh cho lễ tân khi nhận phòng.
            </Text>
          </ListItem>

          <ListItem>
            <Text>
              Guests under 18 years old must be accompanied by adults. Visitors are to present
              personal documents for hotel record. /{'\n'}
              Khách dưới 18 tuổi phải có người lớn đi kèm. Khách thăm phòng phải xuất trình giấy tờ
              tùy thân và đăng ký trực tiếp với lễ tân khách sạn trước khi lên phòng.
            </Text>
          </ListItem>

          <ListItem>
            <Text>
              Visiting time is from 07:00 to 21:00 daily. After 21:00, only registered and reserved
              guests may stay in the room. /{'\n'}
              Thời gian tiếp khách thăm phòng từ 07:00 đến 21:00 hằng ngày. Sau 21:00, chỉ những
              khách đã đăng ký và đặt trước mới có thể lưu trú tại khách sạn.
            </Text>
          </ListItem>

          <ListItem>
            <Text>
              Guests are requested to leave their room key at the Front Desk when leaving the hotel.
              A fee of VND 200,000 per card will be charged for lost keys. /{'\n'}
              Quý khách vui lòng gửi chìa khóa phòng tại quầy lễ tân khi ra ngoài. Trường hợp thất
              lạc chìa khóa phòng chúng tôi sẽ phụ thu 200.000 VNĐ/thẻ.
            </Text>
          </ListItem>

          <ListItem>
            <Text>
              Guests will be held responsible for any damage or loss of hotel property and will be
              charged according to the listed price. /{'\n'}
              Nếu quý khách làm hư hại hoặc mất mát tài sản của khách sạn thì quý khách sẽ phải
              thanh toán theo bảng giá niêm yết.
            </Text>
          </ListItem>

          <ListItem>
            <Text>
              The hotel will not be held responsible for loss of money or valuables left in rooms. /
              {'\n'}
              Khách sạn sẽ không chịu trách nhiệm về việc mất mát tài sản ở trong phòng khách.
            </Text>
          </ListItem>

          <ListItem>
            <Text>
              Please read the information carefully and sign to confirm your acceptance of these
              regulations. Thank you. /{'\n'}
              Quý khách vui lòng đọc kỹ và ký xác nhận nếu đồng ý với các quy định trên. Xin cảm ơn.
            </Text>
          </ListItem>
        </View>
      </View>

      {/* <View style={styles.tableRow}>
        <View
          style={{
            ...styles.tableCell,
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '2px',
            width: '100%',
            paddingTop: '8px'
          }}
        >
          <Text style={styles.text}>
            I agree to pay an additional VND ________________________per night for
            ____________________
          </Text>
          <Text style={styles.text}>
            Total VND_____________________ for_______ nights. Initial: ____________________
          </Text>
        </View>
      </View> */}

      <View style={{ ...styles.tableRow, minHeight: 88 }}>
        <View
          style={{
            ...styles.tableCell,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '50%'
          }}
        >
          <Text style={styles.text}>Guest Signature</Text>
          {signatureGuest && <Image style={styles.image} src={signatureGuest} />}
        </View>
        <View
          style={{
            ...styles.tableCell,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '50%'
          }}
        >
          <Text style={styles.text}>Front Desk Agent</Text>
          {signatureStaff && <Image style={styles.image} src={signatureStaff} />}
        </View>
      </View>
    </View>
  );

  return (
    <Document title={t('pdf.confirmation')} language="vi">
      <Page wrap={false} size="A4" style={styles.page}>
        <HotelInfo />
        <TableInfo />
      </Page>
    </Document>
  );
}
